import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import HeroVideo from './components/heroVideo'

import { LogoSyncContext } from 'src/components/common/AnnotationContext'

import { defaultCTAURI } from 'src/modules/utils/constants'

import { useSelector } from 'react-redux'
import { selectParams, selectCTAParams } from 'src/modules/reducers/params'
import { createUrl } from 'src/modules/utils/createUrl'
import CTAButton from 'src/components/common/CTAButtonNew'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import CTAButtonOfferCode from 'src/components/common/CTAButtonOfferCode'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import Image from '../Image'
import { useCtaText } from 'src/modules/hooks/useCtaText'

// Design System imports
import {
  colors,
  spacing,
  heading,
  body,
  typographyMixin,
  fadeGradients,
  mediaQuery,
  breakpoints,
  desktopBreak,
} from 'src/constants/marketing-ds'

// Text utilities

// Custom hook for annotation logic
const useAnnotation = ({ staticAnnotation, logoContextValue, params }) => {
  const [annotation, setAnnotation] = useState({
    logoUrl: null,
    logoWhite: null,
    alt: null,
    height: null,
  })

  const [isFadeIn, setIsFadeIn] = useState(false)

  useEffect(() => {
    staticAnnotation
      ? setAnnotation(staticAnnotation)
      : logoContextValue && setAnnotation(logoContextValue)
  }, [logoContextValue, staticAnnotation, params?.annotation])

  // Memoize the annotation component to prevent unnecessary re-renders
  const annotationComponent = useMemo(() => {
    if (params?.annotation === 'false' || !annotation?.logoUrl) {
      return null
    }

    return (
      <AnnotationImage
        src={annotation.logoWhite || annotation.logoUrl}
        alt={annotation.alt}
        $logoHeight={annotation.height ? `${annotation.height}px` : '45px'}
        height={annotation.height || 50}
        width="auto"
      />
    )
  }, [annotation, params?.annotation])

  // Handle fade-in animation when annotation changes
  useEffect(() => {
    if (annotationComponent) {
      setIsFadeIn(true)
      const fadeInTimeout = setTimeout(() => {
        setIsFadeIn(false)
      }, 500)
      return () => clearTimeout(fadeInTimeout)
    }
  }, [annotationComponent])

  return { annotation, annotationComponent, isFadeIn }
}

const HeroSection = ({
  lang = 'en',
  market,
  title,
  subtitle,
  tagline,
  ctaText,
  ctaHrefOverride,
  noteText,
  heroBgVideo,
  heroBgVideoMobile,
  containVideo = false,
  // add typecheck staticAnnotation for {logoUrl, alt} and in the future also allow customWidth prop
  staticAnnotation,
  posterImg,
  posterImgMobile,
  hasBanner,
  logo = 'https://assets-imgx.fubo.tv/marketing/lp/images/logos/Fubo_Primary_Logo_FC_RGB.png?w=300',
  logoHeight,

  hasOfferInput,
  // AB Test for Video vs Logo Soup
  noVideo,
  // Fallback for OfferCode and AddonCode
  offerCode,
  addonCode,
  footnote,
  disableFreeTrial,
  //If multiple CTAs are needed, array [{ offerCode, text}]
  heroCTAs,
  isCTADisabled,
  heroSideComponent,
  noCTA = false,
  countdownTimer,
}) => {
  // get ctaParams from redux
  const params = useSelector(selectParams)
  const parsedParams = useSelector(selectCTAParams)

  let ctaURL = createUrl(defaultCTAURI, parsedParams)

  // offerCode and addonCode fallback
  let fallbackParams = {}
  if (offerCode) fallbackParams = { ...fallbackParams, offer: offerCode }
  if (addonCode) fallbackParams = { ...fallbackParams, 'addons[]': addonCode }
  if (
    (!parsedParams?.offerCode && fallbackParams.offer) ||
    (!parsedParams?.addonCode && fallbackParams['addons[]'])
  ) {
    ctaURL = createUrl(defaultCTAURI, { ...fallbackParams, ...parsedParams })
  }

  const logoContextValue = React.useContext(LogoSyncContext)

  // Annotation logic
  const { annotation, annotationComponent, isFadeIn } = useAnnotation({
    staticAnnotation,
    logoContextValue,
    params,
  })

  const stateForProtobuf = useSelector(selectStateForProtobuf)

  const ctaCopy = useCtaText({ lang, market, disableFreeTrial, ctaText })

  // Click Tracking
  const handleOnClick = (url) => {
    const isAnchor = url.startsWith('#')
    fireUIInteractionEvent(
      {
        element: ctaCopy,
        component: 'hero_section_cta_clicked',
        type: isAnchor ? 'clicked_anchor' : 'clicked',
      },
      stateForProtobuf
    )
  }
  const ctaHeroURL = replaceUrlParam(ctaURL, 'comp', 'hero')
  const ctaUrl = ctaHrefOverride || ctaHeroURL

  // CTA button rendering
  const renderCTAButtons = () => {
    if (heroCTAs?.length) {
      return renderMultipleCTAs()
    }
    if (hasOfferInput) {
      return renderOfferCodeCTA()
    }
    return renderDefaultCTA()
  }

  const renderMultipleCTAs = () => (
    <div className="cta-group-container">
      {heroCTAs.map((cta, index) => {
        const buttonLink = cta.offerCode
          ? replaceUrlParam(ctaHeroURL, 'offer', cta.offerCode)
          : cta.link

        return (
          <CTAButton
            key={`hero-cta-${index}`}
            link={buttonLink}
            onClick={() => (cta.onClick ? cta.onClick() : handleOnClick(buttonLink))}
            style={{ width: 'auto', marginRight: 'auto' }}
            isDisabled={isCTADisabled}
          >
            {cta.text}
          </CTAButton>
        )
      })}
    </div>
  )

  const renderOfferCodeCTA = () => (
    <CTAButtonOfferCode
      isDisabled={isCTADisabled}
      link={ctaUrl}
      onClick={() => handleOnClick(ctaUrl)}
    >
      {ctaText || 'Redeem offer'}
    </CTAButtonOfferCode>
  )

  const renderDefaultCTA = () => (
    <CTAButton isDisabled={isCTADisabled} link={ctaUrl} onClick={() => handleOnClick(ctaUrl)}>
      {ctaCopy}
    </CTAButton>
  )

  return (
    <StyledHeroSection hasBanner={hasBanner} className={`hero-wrapper`} $logoHeight={logoHeight}>
      <HeroVideo
        heroBgVideo={heroBgVideo}
        heroBgVideoMobile={heroBgVideoMobile}
        posterImg={posterImg}
        posterImgMobile={posterImgMobile}
        containVideo={containVideo}
        noVideo={noVideo}
      />
      <div className={`content-container ${heroSideComponent ? 'has-side-component' : ''}`}>
        <div className="hero-content">
          <div className="logo-wrapper">
            <Image src={logo} alt="Fubo Logo" width="100%" />
          </div>
          {countdownTimer && <CountdownTimer>{countdownTimer}</CountdownTimer>}
          <Heading>{params.title || title}</Heading>
          <Subtitle>{params.subinfo || subtitle || tagline}</Subtitle>
          {params.priceline ? <p>{params.priceline}</p> : subtitle && tagline && <p>{tagline}</p>}
          {!noCTA && <CTAContainer className="cta-container">{renderCTAButtons()}</CTAContainer>}
          {footnote && <div className="hero-footnote">{footnote}</div>}
          {noteText && <div className="hero-note">{noteText}</div>}
        </div>
        {heroSideComponent && <div className="hero-side-container">{heroSideComponent}</div>}
        {annotation && (
          <div className={'annotation-image bottomRight ' + (isFadeIn ? 'fade-in' : '')}>
            {annotationComponent}
          </div>
        )}
      </div>
    </StyledHeroSection>
  )
}

export default HeroSection

const StyledHeroSection = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background-size: 100%;
  text-align: center;
  width: 100%;
  overflow: hidden;
  position: relative;
  align-items: center;
  background: ${colors.gradients.midnight.medium};

  .content-container {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10;
      width: 110%;
      max-width: 1000px;
      box-sizing: border-box;
      background: ${fadeGradients.hero.small};
      ${mediaQuery.tablet} {
        background: ${fadeGradients.hero.large};
      }
    }

    &.has-side-component {
      .hero-content {
        padding: ${spacing.xxxl} ${spacing.xs} ${spacing.xs};

        ${mediaQuery.tablet} {
          padding: ${spacing.xxxxl} ${spacing.lg} ${spacing.xs};
        }

        ${mediaQuery.desktop} {
          padding: ${spacing.xxxxl} ${spacing.xl};
        }
      }
      .hero-content h2 {
        white-space: normal;
      }
      @media screen and (min-width: 1100px) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        .hero-side-container {
          width: 470px;
        }
      }
    }

    .hero-content {
      padding: ${spacing.xxxl} ${spacing.xs};

      ${mediaQuery.tablet} {
        padding: ${spacing.xxxxl} ${spacing.lg};
      }

      ${mediaQuery.desktop} {
        padding: ${spacing.xxxxl} ${spacing.xl};
        max-width: 645px;
      }

      box-sizing: border-box;
      width: 100%;
      text-align: left;
      position: relative;
      z-index: 10;

      .logo-wrapper {
        z-index: 20;
        width: auto;
        height: ${(props) => props.$logoHeight || '40px'};
        position: relative;
        margin-bottom: ${spacing.xl};

        ${mediaQuery.tablet} {
          height: ${(props) => props.$logoHeight || '56px'};
          width: 700px;
        }

        img {
          max-width: 100%;
          max-height: 100%;
          width: auto;
        }
      }

      .hero-footnote {
        margin-top: ${spacing.md};
        color: ${colors.semantic.text.primary};
        ${typographyMixin(body.sm)}
      }

      p {
        ${typographyMixin(body.sm)}
        color: ${colors.semantic.text.primary};
        width: 100%;

        ${mediaQuery.desktop} {
          ${typographyMixin(body.md)}
        }

        sup {
          font-size: 40%;
        }
      }
    }
    .hero-side-container {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding-top: ${spacing.xs};
      padding-left: ${spacing.xs};
      ${mediaQuery.tablet} {
        padding-left: ${spacing.lg};
      }
      ${mediaQuery.desktop} {
        padding-left: ${spacing.xl};
      }

      z-index: 10;

      margin-bottom: ${spacing.xxl};
      @media screen and (min-width: 1100px) {
        margin-bottom: ${spacing.md};
      }
    }
    .cta-container {
      margin-top: 30px;
      @media (min-width: ${desktopBreak}) {
        margin-top: 60px;
      }

      .cta-group-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        a {
          @media (max-width: 768px) {
            font-size: 16px;
            padding: 15px 30px;
          }
        }
      }
    }
    .hero-note {
      margin-top: ${spacing.md};
      line-height: initial;
      ${typographyMixin(body.sm)}
      color: ${colors.semantic.text.primary};
      span {
        text-decoration: underline;
      }
    }

    @keyframes fade-in {
      0% {
        opacity: 0;
      }
      75% {
        opacity: 0.5;
      }
      100% {
        opacity: 0.5;
      }
    }
    .annotation-image {
      transition: img ease;
      img {
        opacity: 0.5;
      }
      &.fade-in img {
        opacity: 0;
        animation: fade-in 0.48s forwards;
      }
    }
  }
`
const CountdownTimer = styled.div`
  display: inline-block;
`

const Heading = styled.h1`
  ${typographyMixin(heading.xl)}
  color: ${colors.semantic.text.primary};
  width: 100%;
  margin-bottom: ${spacing.md};

  ${mediaQuery.tablet} {
    ${typographyMixin(heading.xxl)}
  }
  sup {
    font-size: 40%;
  }
`

const Subtitle = styled.h2`
  ${typographyMixin(body.md)}
  color: ${colors.semantic.text.primary};
  width: 100%;

  ${mediaQuery.tablet} {
    ${typographyMixin(body.lg)}
  }

  ${mediaQuery.desktop} {
    white-space: nowrap;
  }
`

const CTAContainer = styled.div`
  margin-top: ${spacing.xl};

  ${mediaQuery.tablet} {
    margin-top: ${spacing.xxl};
  }

  .cta-group-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spacing.md};

    a {
      ${typographyMixin(heading.sm)}
      padding: ${spacing.sm} ${spacing.xxl};
      height: ${spacing.xxl};
      display: flex;
      align-items: center;
      justify-content: center;

      ${mediaQuery.tablet} {
        ${typographyMixin(heading.md)}
        padding: ${spacing.md} ${spacing.xxl};
        height: 72px;
      }
    }
  }
`

const AnnotationImage = styled(Image)`
  max-width: 100%;
  width: auto;
  position: absolute;
  z-index: 20;
  right: ${spacing.xs};
  height: auto;
  max-height: ${(props) => (props.$logoHeight ? `calc(${props.$logoHeight} * 5/8)` : '30px')};
  bottom: ${spacing.sm};

  @media (min-width: ${breakpoints.tablet}px) {
    right: ${spacing.lg};
    bottom: ${spacing.md};
    max-height: ${(props) => props.$logoHeight};
  }

  @media (min-width: ${breakpoints.desktop}px) {
    right: ${spacing.xl};
  }
`
